import { useEffect, useState } from 'react';

/**
 * PWA install detection y gestión del prompt nativo.
 *
 * Android/Chrome: escucha `beforeinstallprompt`, guarda el evento y expone
 * un método `promptInstall()` que abre el diálogo nativo.
 *
 * iOS/Safari: NO hay evento. La instalación es manual (Compartir → Añadir a
 * pantalla inicio). Solo podemos detectar plataforma y estado, y mostrar
 * instrucciones al usuario.
 *
 * Estado "installed": true si el usuario ya lo tiene añadido.
 */

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PWAPlatform = 'ios' | 'android' | 'desktop' | 'unknown';

export function detectPlatform(): PWAPlatform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;

  // iOS incluye iPhone, iPad, iPod
  if (/iPhone|iPad|iPod/.test(ua) && !/Windows/.test(ua)) return 'ios';
  // iPadOS 13+ se identifica como Mac con touch
  if (/Macintosh/.test(ua) && 'ontouchend' in document) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  const iosStandalone = (
    window.navigator as unknown as { standalone?: boolean }
  ).standalone === true;
  // Android + PWA general
  const displayModeStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  return iosStandalone || displayModeStandalone;
}

/**
 * Hook con toda la lógica reactiva:
 * - platform: 'ios' | 'android' | 'desktop' | 'unknown'
 * - installed: true si ya está como app (standalone mode)
 * - canPromptNative: true en Android cuando el navegador ha disparado el evento
 * - promptInstall(): abre el diálogo nativo (solo si canPromptNative)
 */
export function useInstallPWA() {
  const [platform] = useState<PWAPlatform>(() => detectPlatform());
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferredPrompt) return 'unavailable';
    await deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return result.outcome;
  };

  return {
    platform,
    installed,
    canPromptNative: !!deferredPrompt,
    promptInstall,
  };
}
